import { useControlledState } from 'react-stately/useControlledState';

export interface PaginationState {
  page: number;
  pageSize: number;
}

export const usePagination = ({
  itemCount,
  page: pageProp,
  defaultPage = 1,
  pageSize: pageSizeProp,
  defaultPageSize = 5,
  onChange,
}: {
  itemCount: number;
  page?: number;
  defaultPage?: number;
  pageSize?: number;
  defaultPageSize?: number;
  onChange?: (newState: PaginationState) => void;
}): {
  currentPageStartIndex: number;
  currentPageEndIndex: number;
  currentPageSize: number;
  currentPage: number;
  totalPages: number;
  toPage: (newPage: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  changePageSize: (newPageSize: number) => void;
} => {
  const [page, setPage] = useControlledState(pageProp!, defaultPage);
  const [pageSize, setPageSize] = useControlledState(
    pageSizeProp!,
    defaultPageSize
  );

  const totalPages = Math.ceil(itemCount / pageSize);

  const currentPageStartIndex = Math.max((page - 1) * pageSize + 1, 0);
  const currentPageEndIndex = Math.min(page * pageSize, itemCount);

  const updateState = ({ page, pageSize }: PaginationState) => {
    onChange?.({ page, pageSize });
    setPage(page);
    setPageSize(pageSize);
  };

  return {
    currentPageStartIndex,
    currentPageEndIndex,
    currentPageSize: pageSize,
    currentPage: page,
    totalPages,
    toPage: (newPage: number) => {
      updateState({
        page: Math.min(Math.max(newPage, 1), totalPages),
        pageSize,
      });
    },
    nextPage: () => {
      const newPage = Math.min(page + 1, totalPages);
      updateState({ page: newPage, pageSize });
    },

    prevPage: () => {
      const newPage = Math.max(page - 1, 1);
      updateState({ page: newPage, pageSize });
    },

    changePageSize: (newPageSize: number) => {
      const newTotalPages = Math.ceil(itemCount / newPageSize);
      const newPage = Math.min(
        Math.floor(currentPageStartIndex / newPageSize) + 1,
        newTotalPages
      );
      updateState({ page: newPage, pageSize: newPageSize });
    },
  };
};
